import { addListener } from "../../../../../../resources/js/utils";
import ProductActions  from "../mixin/ProductAction";

/**
 * This RelatedProducts widget displays slides that bleed off the right and left edges of the window.
 * They extend beyond their container, so calculations must take into account the screen/browser window width.
 */
class RelatedProducts
{
    // Registry to hold widget instances indexed by DOM elements
    static widgetInstances = new Map();

    constructor(carousel)
    {
        this.setIndicators = this.setIndicators.bind(this);
        this.animation = this.animation.bind(this);
        this.touchStart = this.touchStart.bind(this);
        this.touchMove = this.touchMove.bind(this);
        this.touchEnd = this.touchEnd.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.mouseStart = this.mouseStart.bind(this);
        this.mouseMove = this.mouseMove.bind(this);
        this.mouseEnd = this.mouseEnd.bind(this);

        this.carousel = carousel;
        this.carouselContainer = this.carousel.parentElement;
        this.animationID = 0;
        this.maxTranslate = 0;
        this.currentTranslate = 0;

        this.init();

        RelatedProducts.widgetInstances.set(carousel, this);
    }

    init()
    {
        this.slideIndex = 0;
        this.prevTranslate = 0;
        this.prevBtn = this.carouselContainer.querySelector(".prev-button");
        this.nextBtn = this.carouselContainer.querySelector(".next-button");

        this.setIndicators();

        this.setListeners();
    }

    //##########################
    //####  Mouse clicks  ######
    //##########################
    setIndicators()
    {
        this.carouselWidth = this.carousel.offsetWidth;

        // Clear the extra slide indicators if we end up with too many.
        this.carouselContainer.querySelector(".itemIndicators").innerHTML = '';

        // All the sliders should be the same width, but we calculate each of them anyway.
        this.totalSlideCount = 0;
        this.totalSlideWidth = 0;
        Array.from(this.carousel.children).forEach((child) => {

            const style = window.getComputedStyle(child);
            this.slideMarginL = parseInt(style.marginLeft, 10);
            this.slideMarginR = parseInt(style.marginRight, 10);
            const totalWidth = child.offsetWidth + this.slideMarginL + this.slideMarginR;
            this.totalSlideWidth += totalWidth;

            this.totalSlideCount ++;
        });

        // Calculate the number of indicators by counting the number of slides that are partially or wholly off-screen.
        let viewportWidth = document.documentElement.clientWidth;
        let containerMargin = (viewportWidth - this.carouselWidth) / 2;

        let hiddenLength = this.totalSlideWidth - this.carouselWidth - containerMargin;
        this.slideWidth = Math.round(this.totalSlideWidth / this.totalSlideCount);
        let indicatorCount = Math.ceil( hiddenLength / this.slideWidth ) + 1; // Include an indicator for the active/visible slide.
        if (indicatorCount < 1) indicatorCount = 1;
        // this.maxTranslate = -( (indicatorCount - 1) * (this.slideWidth + this.slideMarginL + this.slideMarginR));
        this.maxTranslate = -(this.totalSlideWidth - this.slideWidth);




        // Generate the indicators and render them on screen.
        for ( let i= 0; i < indicatorCount; i++ ) {
            const indicator = document.createElement("span");
            indicator.className = "cIndicator cursor-pointer";
            indicator.innerHTML = `
<svg width="21" height="8" viewBox="0 0 21 4" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect opacity="0.15" x="0.289062" width="21" height="4" fill="#2A2F33"/>
</svg>
`;
            if ( i === this.slideIndex )
                indicator.children[0].children[0].setAttribute("opacity", "1");
            else
                indicator.children[0].children[0].setAttribute("opacity", "0.15");

            this.carouselContainer.querySelector(".itemIndicators").appendChild(indicator);

            // Add listener so the user can click the indicator and cause the carousel to scroll
            addListener('click', indicator, () => this.setSlide( i ));
        }

        this.indicators = Array.from(this.carouselContainer.querySelector(".itemIndicators").children);

        if ( this.slideIndex > indicatorCount) {
            this.slideIndex = indicatorCount - 1;
            this.indicators[this.slideIndex].children[0].children[0].setAttribute("opacity", "1");
        }
    }

    setListeners()
    {
        // Set the listeners connected to this carousel
        addListener('resize', window, this.setIndicators);

        // Touch event listeners
        addListener('touchstart', this.carousel, this.touchStart);
        addListener('touchmove', this.carousel, this.touchMove);
        addListener('touchend', this.carousel, this.touchEnd);

        // Mouse wheel event listener
        addListener('wheel', this.carousel, this.handleWheel);

        // Mouse event listeners
        addListener('mousedown', this.carousel, this.mouseStart);
        addListener('mousemove', this.carousel, this.mouseMove);
        addListener('mouseup', this.carousel, this.mouseEnd);
        addListener('mouseleave', this.carousel, this.mouseEnd);

        ProductActions.setAllProductListeners();

        addListener('click', this.prevBtn, () => this.slide( -1));
        addListener('click', this.nextBtn, () => this.slide( 1));
    }

    slide(direction)
    {
        // If the indicator line can move in the `direction`, then move by one.
        for ( let index = 0; index < this.indicators.length; index++ ) {
            if (this.slideIndex === index) {
                // this is the current position.
                // Must be >= 0 and <= this.indicators - 1.
                let newIndex = Math.min( (this.indicators.length - 1), Math.max(0, index + direction));
                if ( newIndex === 0 || this.indicators[newIndex] !== undefined ) {
                    // Move the images in that direction.
                    this.slideIndex = newIndex;
                    this.currentTranslate = -(this.slideIndex * this.slideWidth);
                    // this.currentTranslate can never be greater than zero
                    if ( this.currentTranslate > 0 ) this.currentTranslate = 0;

                    this.setSliderPosition();

                    this.indicators[index].children[0].children[0].setAttribute("opacity", "0.15");
                    this.indicators[newIndex].children[0].children[0].setAttribute("opacity", "1");

                    break;
                }
            }
        }
    }

    setSlide(position)
    {
        // Reset the opacity on all indicators
        this.indicators.forEach((indicator) => {
            indicator.children[0].children[0].setAttribute("opacity", "0.15");
        });

        this.slideIndex = position;
        this.currentTranslate = -(this.slideIndex * this.slideWidth);
        this.carousel.style.transform = `translateX(${ this.currentTranslate }px)`;

        this.indicators[position].children[0].children[0].setAttribute("opacity", "1");
    }

    //############################
    //####  Touch gestures  ######
    //############################
    touchStart(event)
    {
        this.isDragging = true;
        this.startPos = this.getPositionX(event);
        this.animationID = requestAnimationFrame(this.animation);
        this.carousel.classList.add('cursor-grabbing');
    }

    touchMove(event)
    {
        if (this.isDragging) {
            let currentPosition = this.getPositionX(event);
            this.currentTranslate = this.prevTranslate + currentPosition - this.startPos;
        }
    }

    touchEnd()
    {
        cancelAnimationFrame(this.animationID);
        this.isDragging = false;
        this.carousel.classList.remove('cursor-grabbing');

        // Determine which direction the touch event moved the slides, and set the position
        if ( this.prevTranslate > this.currentTranslate ) {
            this.slide(1)
        }
        else {
            this.slide(-1)
        }

        this.prevTranslate = this.currentTranslate;
    }

    getPositionX(event)
    {
        return event.touches[0].clientX;
    }

    animation()
    {
        this.setSliderPosition();
        if (this.isDragging) requestAnimationFrame(this.animation);
    }

    //############################
    //####    Mouse wheel   ######
    //############################
    handleWheel(event) {
        // Only capture if the wheel is spinning horizontally.
        if (Math.abs(event.deltaX) > 0) {
            // Prevent the default scroll behavior to manually handle horizontal scrolling
            event.preventDefault();

            // Adjust the carousel position based on the horizontal scroll amount (deltaX)
            const scrollAmount = event.deltaX;

            // Calculate the new translate value, ensuring it doesn't go beyond the bounds
            let newTranslate = this.currentTranslate + scrollAmount;
            newTranslate = Math.min(newTranslate, 0); // Prevent scrolling past the first item
            newTranslate = Math.max(newTranslate, this.maxTranslate); // Prevent scrolling past the last item
            this.currentTranslate = newTranslate;

            if ( this.prevTranslate > this.currentTranslate ) {
                this.slide(1)
            }
            else if ( this.prevTranslate < this.currentTranslate ) {
                this.slide(-1)
            }

            this.prevTranslate = this.currentTranslate;
        }
    }

    //############################
    //####    Mouse move    ######
    //############################
    mouseStart(event)
    {
        this.isDragging = true;
        this.startPos = event.clientX;
        this.animationID = requestAnimationFrame(this.animation);
        this.carousel.classList.add('cursor-grabbing');
        event.preventDefault(); // Prevent text selection
    }

    mouseMove(event)
    {
        if (!this.isDragging) return;
        let currentPosition = event.clientX;
        this.currentTranslate = this.prevTranslate + currentPosition - this.startPos;
        event.preventDefault(); // Prevent text selection
    }

    mouseEnd()
    {
        if (!this.isDragging) return;

        this.touchEnd();
    }


    //############################
    //####  Support Methods ######
    //############################
    setSliderPosition()
    {
        this.carousel.style.transform = `translateX(${this.currentTranslate}px)`;
    }

    // Static method to find all related elements and instantiate the class for each
    static initAll() {
        const widgets = document.querySelectorAll('[data-br-widget="relatedProducts"]');
        widgets.forEach(widgetElement => {
            if (!RelatedProducts.widgetInstances.has(widgetElement)) {
                RelatedProducts.widgetInstances.set(widgetElement, new RelatedProducts(widgetElement));
            }
        });
    }

    // Static method to access a widget instance from a DOM element
    static getInstance(element) {
        return RelatedProducts.widgetInstances.get(element);
    }
}

export default RelatedProducts;